import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from '../api/client';
import toast from 'react-hot-toast';

const ZODIAC_SYMBOLS = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
};

const ZODIAC_TURKISH = {
  Aries: 'Koç', Taurus: 'Boğa', Gemini: 'İkizler', Cancer: 'Yengeç',
  Leo: 'Aslan', Virgo: 'Başak', Libra: 'Terazi', Scorpio: 'Akrep',
  Sagittarius: 'Yay', Capricorn: 'Oğlak', Aquarius: 'Kova', Pisces: 'Balık',
};

const LANG_LABELS = { en: '🇬🇧 English', tr: '🇹🇷 Türkçe', de: '🇩🇪 Deutsch' };

function ScoreBar({ label, score, max = 10, color = 'indigo' }) {
  const pct = Math.round((score / max) * 100);
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs text-slate-400 mb-1">
        <span>{label}</span>
        <span>{score}/{max}</span>
      </div>
      <div className="w-full bg-slate-700 rounded-full h-2">
        <div
          className={`bg-${color}-500 h-2 rounded-full transition-all duration-500`}
          style={{ width: `${pct}%`, backgroundColor: color === 'indigo' ? '#6366f1' : color === 'pink' ? '#ec4899' : color === 'green' ? '#22c55e' : '#eab308' }}
        />
      </div>
    </div>
  );
}

function PredictionCard({ title, icon, prediction }) {
  if (!prediction) return null;
  const generalText = prediction.general_prediction || prediction.general;
  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
      <h4 className="text-sm font-semibold text-slate-300 mb-3">{icon} {title}</h4>
      {generalText && (
        <p className="text-slate-300 text-sm mb-3 leading-relaxed">{generalText}</p>
      )}
      <div className="space-y-2">
        {prediction.love && <p className="text-xs text-slate-400">{prediction.love}</p>}
        {prediction.career && <p className="text-xs text-slate-400">{prediction.career}</p>}
        {prediction.health && <p className="text-xs text-slate-400">{prediction.health}</p>}
        {prediction.advice && (
          <p className="text-xs text-yellow-400/80 mt-2 pt-2 border-t border-slate-700">💡 {prediction.advice}</p>
        )}
      </div>
      {(prediction.love_score || prediction.career_score || prediction.luck_score) && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <ScoreBar label="Aşk" score={prediction.love_score || 0} color="pink" />
          <ScoreBar label="Kariyer" score={prediction.career_score || 0} color="indigo" />
          <ScoreBar label="Şans" score={prediction.luck_score || 0} color="yellow" />
        </div>
      )}
    </div>
  );
}

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatTotal, setChatTotal] = useState(0);
  const [chatPage, setChatPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('horoscope');
  const [expandedPastDay, setExpandedPastDay] = useState(null);

  const chatLimit = 30;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`/api/v1/admin/predictions/user/${id}/dashboard`);
      setData(res.data);
    } catch (err) {
      toast.error('Kullanıcı verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchChat = useCallback(async () => {
    setChatLoading(true);
    try {
      const res = await client.get(
        `/api/v1/admin/chat-logs?user_id=${id}&skip=${chatPage * chatLimit}&limit=${chatLimit}`
      );
      setChatMessages(res.data.messages || []);
      setChatTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Sohbet logları yüklenemedi');
    } finally {
      setChatLoading(false);
    }
  }, [id, chatPage]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchChat(); }, [fetchChat]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-400">Yükleniyor...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-400">Kullanıcı bulunamadı</p>
        <button onClick={() => navigate('/users')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded text-sm">
          ← Kullanıcılara Dön
        </button>
      </div>
    );
  }

  const { profile, daily, weekly_monthly, past_days, sign, lang, user_timezone } = data;
  const tabs = [
    { key: 'horoscope', label: '🔮 Burç Yorumları' },
    { key: 'chat', label: `💬 AI Sohbet (${chatTotal})` },
  ];

  const chatMaxPages = Math.ceil(chatTotal / chatLimit);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/users')}
          className="px-3 py-1 bg-slate-700 text-slate-300 rounded text-sm hover:bg-slate-600"
        >
          ← Geri
        </button>
        <h2 className="text-2xl font-bold text-white">
          {ZODIAC_SYMBOLS[sign] || '🌟'} {profile.full_name || profile.email}
        </h2>
        <span className={`px-2 py-1 rounded text-xs font-semibold ${
          profile.subscription_tier === 'premium' ? 'bg-indigo-600 text-white' :
          profile.subscription_tier === 'premium_plus' ? 'bg-purple-600 text-white' :
          'bg-slate-600 text-slate-300'
        }`}>
          {profile.subscription_tier}
        </span>
        <span className="text-xs text-slate-400">
          {LANG_LABELS[profile.language] || profile.language}
        </span>
      </div>

      {/* Profile Card */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-5 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-slate-500 text-xs">E-posta</p>
            <p className="text-white">{profile.email}</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Big Three</p>
            <p className="text-white">
              ☀️ {ZODIAC_TURKISH[profile.sun_sign] || profile.sun_sign} · 🌙 {ZODIAC_TURKISH[profile.moon_sign] || profile.moon_sign || '—'} · ⬆️ {ZODIAC_TURKISH[profile.rising_sign] || profile.rising_sign || '—'}
            </p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Doğum</p>
            <p className="text-white">
              {profile.birth_date || '—'} {profile.birth_time ? `· ${profile.birth_time}` : ''} · {profile.birth_city || '—'}
            </p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Koordinatlar</p>
            <p className="text-white">
              {profile.birth_latitude ? `${profile.birth_latitude.toFixed(2)}° / ${profile.birth_longitude?.toFixed(2)}°` : '—'}
            </p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Timezone</p>
            <p className="text-white">{profile.timezone || '—'} <span className="text-xs text-slate-500">(bugün = {data.today})</span></p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Kredi / AI Kullanım</p>
            <p className="text-white">{profile.credits} kredi · {profile.daily_ai_count || 0} AI kullanım</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Kayıt Tarihi</p>
            <p className="text-white">{profile.created_at ? new Date(profile.created_at).toLocaleDateString('tr-TR') : '—'}</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Son Aktiflik</p>
            <p className="text-white">{profile.last_active_at ? new Date(profile.last_active_at).toLocaleString('tr-TR') : '—'}</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Referral Kodu</p>
            <p className="text-white font-mono">{profile.referral_code || '—'}</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Misafir?</p>
            <p className="text-white">{profile.is_guest ? '✅ Evet' : '❌ Hayır'}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-lg transition text-sm ${
              activeTab === t.key
                ? 'bg-indigo-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'horoscope' && (
        <div>
          {/* Today's prediction */}
          <h3 className="text-lg font-semibold text-white mb-3">📅 Bugünkü Yorum — {data.today} <span className="text-sm text-slate-500 font-normal">({user_timezone || 'Europe/Istanbul'})</span></h3>
          {daily ? (
            <PredictionCard title="Günlük Yorum" icon={ZODIAC_SYMBOLS[sign]} prediction={daily.prediction} />
          ) : (
            <div className="bg-slate-800/50 rounded-lg border border-slate-700/50 p-8 text-center">
              <p className="text-slate-500 text-sm">Bu kullanıcı için bugün henüz yorum üretilmemiş</p>
              <p className="text-slate-600 text-xs mt-1">{sign} · {lang}</p>
              {data.available_cache_dates?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-700">
                  <p className="text-xs text-slate-500 mb-2">📦 Mevcut cache kayıtları ({sign}):</p>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {data.available_cache_dates.map((d, i) => (
                      <span key={i} className="px-2 py-0.5 bg-slate-700 text-slate-400 text-xs rounded">
                        {d.date} ({d.lang})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Weekly/Monthly */}
          {weekly_monthly && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-white mb-3">📊 Haftalık / Aylık</h3>
              <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                {weekly_monthly.weekly_summary && (
                  <div className="mb-3">
                    <p className="text-xs text-slate-500 mb-1">Haftalık Özet</p>
                    <p className="text-sm text-slate-300">{weekly_monthly.weekly_summary}</p>
                  </div>
                )}
                {weekly_monthly.weekly_highlights && (
                  <div className="mb-3 space-y-1">
                    {weekly_monthly.weekly_highlights.map((h, i) => (
                      <p key={i} className="text-xs text-slate-400">• {h}</p>
                    ))}
                  </div>
                )}
                {weekly_monthly.monthly_summary && (
                  <div className="pt-3 border-t border-slate-700">
                    <p className="text-xs text-slate-500 mb-1">Aylık Özet</p>
                    <p className="text-sm text-slate-300">{weekly_monthly.monthly_summary}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Past Days */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-white mb-3">📆 Geçmiş 7 Gün</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {past_days?.map((pd, i) => (
                <div
                  key={pd.date}
                  className={`rounded-lg border p-3 cursor-pointer transition ${
                    pd.found
                      ? 'bg-slate-800 border-slate-700 hover:border-indigo-500'
                      : 'bg-slate-800/30 border-slate-700/30'
                  }`}
                  onClick={() => pd.found && setExpandedPastDay(expandedPastDay === i ? null : i)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white font-medium">{pd.date}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${pd.found ? 'bg-green-900/40 text-green-400' : 'bg-slate-700 text-slate-500'}`}>
                      {pd.found ? 'Cache ✓' : 'Yok'}
                    </span>
                  </div>
                  {pd.found && pd.prediction?.general_prediction && (
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                      {pd.prediction.general_prediction}
                    </p>
                  )}
                  {expandedPastDay === i && pd.prediction && (
                    <div className="mt-3 pt-3 border-t border-slate-700 space-y-1">
                      {pd.prediction.love && <p className="text-xs text-slate-400">{pd.prediction.love}</p>}
                      {pd.prediction.career && <p className="text-xs text-slate-400">{pd.prediction.career}</p>}
                      {pd.prediction.health && <p className="text-xs text-slate-400">{pd.prediction.health}</p>}
                      {pd.prediction.advice && <p className="text-xs text-yellow-400/70">💡 {pd.prediction.advice}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'chat' && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">💬 AI Sohbet Geçmişi</h3>
          <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto p-4 space-y-3">
              {chatLoading ? (
                <p className="text-slate-400 text-center py-8">Yükleniyor...</p>
              ) : chatMessages.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Bu kullanıcının sohbet geçmişi yok</p>
              ) : (
                chatMessages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] rounded-lg px-4 py-2.5 ${
                      msg.role === 'user'
                        ? 'bg-indigo-600/30 border border-indigo-500/30'
                        : 'bg-slate-700 border border-slate-600'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-indigo-300' : 'text-purple-300'}`}>
                          {msg.role === 'user' ? '👤 Kullanıcı' : '🌟 Astra'}
                        </span>
                        <span className="text-xs text-slate-500">
                          {msg.created_at ? new Date(msg.created_at).toLocaleString('tr-TR') : ''}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            {chatTotal > chatLimit && (
              <div className="px-4 py-3 border-t border-slate-700 flex items-center justify-between">
                <span className="text-slate-400 text-xs">
                  {chatPage * chatLimit + 1}–{Math.min((chatPage + 1) * chatLimit, chatTotal)} / {chatTotal}
                </span>
                <div className="space-x-2">
                  <button
                    onClick={() => setChatPage(Math.max(0, chatPage - 1))}
                    disabled={chatPage === 0}
                    className="px-3 py-1 bg-slate-700 text-white text-xs rounded disabled:opacity-50"
                  >
                    ← Önceki
                  </button>
                  <button
                    onClick={() => setChatPage(chatPage + 1)}
                    disabled={chatPage >= chatMaxPages - 1}
                    className="px-3 py-1 bg-slate-700 text-white text-xs rounded disabled:opacity-50"
                  >
                    Sonraki →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
